import { useDebouncedCallback } from "@/lib/useDebouncedCallback";
import { TRIGGER_DEFINITIONS, isNotificationTriggerType } from "@/v2/entities/notifications/NotificationTriggers.model";
import { useSlackNotificationSettings, useUpdateSlackNotificationSetting } from "@/v2/entities/notifications/SlackNotifications.api";
import { isAxiosError } from "axios";
import { useCallback, useMemo, useState } from "react";
import { toast } from "sonner";
import { useAuth0 } from "@auth0/auth0-react";

function getErrorMessage(error: Error): string {
  if (isAxiosError(error) && error.response?.data?.message) {
    return error.response.data.message;
  }
  return "Failed to save notification setting";
}

export interface NotificationTrigger {
  trigger: string;
  title: string;
  description: string;
  enabled: boolean;
  channelId?: string;
}

export function useSlackNotifications() {
  const { user } = useAuth0();
  const { data: settings, isLoading, error: settingsError } = useSlackNotificationSettings();
  const updateSetting = useUpdateSlackNotificationSetting();
  
  const userTimezone = useMemo(() => {
    return user?.zoneinfo || Intl.DateTimeFormat().resolvedOptions().timeZone;
  }, [user]);
  
  const [userInputs, setUserInputs] = useState<Record<string, string>>({});

  const setEnabled = useCallback(
    (trigger: string, enabled: boolean, onError?: (error: Error) => void) => {
      const payload: { trigger: string; enabled: boolean; timezone?: string } = { 
        trigger, 
        enabled,
        timezone: userTimezone
      };
      
      updateSetting.mutate(
        payload,
        {
          onError: (error: Error) => {
            onError?.(error);
          }
        }
      );
    },
    [updateSetting, userTimezone]
  );

  const setChannelId = useCallback(
    (trigger: string, channelId?: string, onError?: (error: Error) => void) => {
      const normalizedChannelId = channelId?.trim() || "";
      const invalidChannelId = normalizedChannelId && !normalizedChannelId.startsWith('C');
      // If channel ID is being removed or doesn't start with C, disable the notification
      const shouldDisable = !normalizedChannelId || invalidChannelId;
      
      // Show toast if channel ID becomes invalid and notification is currently enabled
      if (shouldDisable) {
        const currentSetting = settings?.find(s => s.trigger === trigger);
        if (currentSetting?.enabled) {
          if (invalidChannelId) {
            toast.error("Invalid Slack channel ID format. Channel IDs should start with 'C'.");
          } else {
            toast.error("Channel ID is required. Notification has been disabled.");
          }
        }
      }
      
      const payload: { trigger: string; channelId: string; enabled?: boolean; timezone?: string } = { 
        trigger, 
        channelId: normalizedChannelId,
        enabled: shouldDisable ? false : undefined,
        timezone: userTimezone
      };
      
      updateSetting.mutate(
        payload,
        {
          onError: (error: Error) => {
            onError?.(error);
          }
        }
      );
    },
    [updateSetting, settings, userTimezone]
  );

  const rows = useMemo((): NotificationTrigger[] => {
    // Always show all defined triggers
    const allTriggers = Object.keys(TRIGGER_DEFINITIONS).filter(isNotificationTriggerType);
    
    // Safety check: ensure settings is an array
    const safeSettings = Array.isArray(settings) ? settings : [];
    
    return allTriggers.map((trigger) => {
      // Find the setting for this trigger from the API response
      const setting = safeSettings.find(s => s.trigger === trigger);
      
      return {
        trigger: trigger,
        title: TRIGGER_DEFINITIONS[trigger].title || trigger,
        description: TRIGGER_DEFINITIONS[trigger].description || "",
        enabled: setting?.enabled || false,
        channelId: setting?.channelId || undefined,
      };
    });
  }, [settings]);

  // Update rows with user input, needed because of the debouncing added to the channel ID changes
  const displayRows = useMemo(() => {
    return rows.map(row => ({
      ...row,
      channelId: userInputs[row.trigger] ?? row.channelId
    }));
  }, [rows, userInputs]);

  // Debounced function to update channel ID with error handling
  const debouncedSetChannelId = useDebouncedCallback(
    useCallback((trigger: string, channelId: string, onError?: (error: Error) => void) => {
      setChannelId(trigger, channelId, onError);
    }, [setChannelId]),
    500
  );

  // Handle channel ID changes with debouncing and error handling
  const handleChannelIdChange = useCallback((trigger: string, value: string) => {
    setUserInputs(prev => ({ ...prev, [trigger]: value }));
    
    // Add error handling for channel ID updates
    debouncedSetChannelId(trigger, value, (error: Error) => {
      const errorMessage = getErrorMessage(error);
      toast.error(errorMessage);
    });
  }, [debouncedSetChannelId]);

  // Handle switch changes with error handling
  const handleSwitchChange = useCallback((notification: { trigger: string; channelId?: string }, checked: boolean) => {
    if (updateSetting.isLoading && updateSetting.variables?.trigger === notification.trigger) return;
    
    setEnabled(notification.trigger, checked, (error: Error) => {
      const errorMessage = getErrorMessage(error);
      toast.error(errorMessage);
    });
  }, [setEnabled, updateSetting.isLoading, updateSetting.variables?.trigger]);

  return { 
    rows: displayRows, 
    handleChannelIdChange,
    handleSwitchChange,
    isLoading,
    isUpdating: updateSetting.isLoading,
    updatingTrigger: updateSetting.isLoading && updateSetting.variables?.trigger ? updateSetting.variables.trigger : null,
    error: updateSetting.error || settingsError
  };
}
